import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Icon, statusMeta } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function UserDashboard() {
  const { user } = useAuth();
  const [tags, setTags] = useState(null);

  useEffect(() => {
    api.get("/tags").then((r) => setTags(r.data.data)).catch(() => setTags([]));
  }, []);

  const lostCount = tags?.filter((t) => t.status === "LOST").length || 0;

  return (
    <div className="px-5 pt-8">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-muted-foreground text-sm">Welcome back,</p>
          <h1 className="font-display font-black text-3xl tracking-tight">{user?.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/app/profile" className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold">
          {user?.name?.[0]?.toUpperCase()}
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <div className="rounded-2xl border border-border p-4">
          <p className="text-3xl font-display font-black">{tags?.length ?? "—"}</p>
          <p className="text-sm text-muted-foreground">My tags</p>
        </div>
        <div className="rounded-2xl border border-border p-4" style={{ borderColor: lostCount ? "#FF3B30" : undefined }}>
          <p className="text-3xl font-display font-black" style={{ color: lostCount ? "#FF3B30" : undefined }}>{lostCount}</p>
          <p className="text-sm text-muted-foreground">Reported lost</p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-8 mb-3">
        <h2 className="font-display font-bold text-xl">My Tags</h2>
        <Link to="/claim" data-testid="add-tag-link" className="tap text-sm font-semibold text-status-active flex items-center gap-1">
          <Icon name="Plus" className="w-4 h-4" /> Add
        </Link>
      </div>

      {tags === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : tags.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center mt-2">
          <Icon name="QrCode" className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="font-semibold mt-3">No tags yet</p>
          <p className="text-sm text-muted-foreground mt-1">Claim your first tag with its authentication code.</p>
          <Link to="/claim" className="tap inline-flex mt-4 px-6 py-3 rounded-full bg-primary text-primary-foreground font-bold">Claim a tag</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {tags.map((t) => {
            const meta = statusMeta(t.status);
            return (
              <Link
                key={t.id}
                to={`/app/tags/${t.id}`}
                data-testid={`tag-card-${t.tag_number}`}
                className="tap block rounded-2xl border border-border p-4 hover:bg-secondary/60"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: meta.bg, color: meta.color }}>
                    <Icon name={t.category?.icon || "Tag"} className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{t.category?.name || "Tag"}</p>
                    <p className="text-sm text-muted-foreground font-mono">{t.tag_number}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: meta.bg, color: meta.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
                    {meta.label}
                  </span>
                  <Icon name="ChevronRight" className="w-5 h-5 text-muted-foreground shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
